import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const YEAR_RANGE = 15;

const MonthPicker = ({ monthlyData, setShowMonthPicker, onSelectMonth, getCurrentMonthKey }) => {
  const currentKey = getCurrentMonthKey();
  const currentYear = parseInt(currentKey.split('-')[0], 10);
  const [year, setYear] = useState(currentYear);

  const hasData = (monthKey) => monthlyData.some(m => m.month === monthKey);

  return (
    <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl w-full max-w-sm flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-gray-900 font-semibold text-lg tracking-tight">Select Month</h2>
          <button onClick={() => setShowMonthPicker(false)} className="text-gray-400 hover:text-gray-900 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Year navigation */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <button
            onClick={() => setYear(y => Math.max(currentYear - YEAR_RANGE, y - 1))}
            disabled={year <= currentYear - YEAR_RANGE}
            className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-gray-900 font-semibold tracking-tight">{year}</span>
          <button
            onClick={() => setYear(y => Math.min(currentYear + YEAR_RANGE, y + 1))}
            disabled={year >= currentYear + YEAR_RANGE}
            className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-2 p-5">
          {MONTH_LABELS.map((label, i) => {
            const monthKey = `${year}-${String(i + 1).padStart(2, '0')}`;
            const isCurrent = monthKey === currentKey;
            const monthHasData = hasData(monthKey);

            return (
              <button
                key={monthKey}
                onClick={() => onSelectMonth(monthKey)}
                className={`relative flex flex-col items-center justify-center gap-1 py-3 rounded-xl text-sm border transition-colors ${
                  isCurrent
                    ? 'border-accent bg-emerald-50 text-accent font-medium'
                    : monthHasData
                      ? 'border-gray-200 text-gray-900 hover:bg-gray-50'
                      : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                }`}
              >
                {label}
                {monthHasData && (
                  <span className={`w-1 h-1 rounded-full ${isCurrent ? 'bg-accent' : 'bg-gray-400'}`} />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-4 pb-5 text-xs text-gray-400">
          <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-gray-400" />Has data</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full border border-accent" />Current month</span>
        </div>
      </div>
    </div>
  );
};

export default MonthPicker;
